// 前提：source/js/main.js の DOMContentLoaded 内で tabs() を呼び出すこと
//   import { tabs } from './modules/tabs';
//   document.addEventListener('DOMContentLoaded', () => { tabs(); });
//
// HTML 仕様：
// - .js-tabs ブロック内に .js-tabs__tablist / .js-tabs__tab / .js-tabs__panel を配置
// - 各 .js-tabs__tab に aria-controls="<panel-id>" を指定
// - 各 .js-tabs__panel に id="<panel-id>" を指定
// - 初期非表示パネルには hidden 属性を付ける
// - URL クエリ同期したい場合はコンテナに id 属性を付ける（id がパラメータキーになる）
// - スクロール調整したい場合は .js-tabs--scroll クラスをコンテナに追加

/**
 * タブの初期化
 * - クリックでタブ切替（hidden 属性で表示制御）
 * - ARIA フル対応（role / aria-selected / aria-controls / aria-labelledby / tabindex 自動付与）
 * - キーボードナビ（←→↑↓ Home End Enter Space）
 * - URL クエリ同期（?<containerId>=<panelId>）
 * - popstate 対応（戻る/進むで切替）
 * - js-tabs--scroll モディファイア時のみタブクリックでスクロール調整
 */
const tabs = () => {
  const tabGroups = document.querySelectorAll('.js-tabs');
  if (!tabGroups.length) return;

  const urlParams = new URLSearchParams(window.location.search);

  tabGroups.forEach((tabsContainer) => {
    const tabList = tabsContainer.querySelector('.js-tabs__tablist');
    const tabGroupId = tabsContainer.getAttribute('id');

    // tablist ロール自動付与
    if (tabList && !tabList.hasAttribute('role')) {
      tabList.setAttribute('role', 'tablist');
    }

    const tabItems = tabsContainer.querySelectorAll('.js-tabs__tab');
    const panels = tabsContainer.querySelectorAll('.js-tabs__panel');
    if (!tabItems.length || !panels.length) return;

    // 初期アクティブタブを特定（クエリパラメータ → aria-selected=true → 最初のタブ）
    const activeTabId = tabGroupId ? urlParams.get(tabGroupId) : null;
    let activeTabIndex = -1;

    if (activeTabId) {
      // パネル id がパラメータと一致するか確認
      const paramPanelIndex = Array.from(panels).findIndex(
        (panel) => panel.id === activeTabId,
      );
      if (paramPanelIndex !== -1) {
        activeTabIndex = paramPanelIndex;
      } else {
        // aria-controls がパラメータと一致するタブを探す
        const paramTabIndex = Array.from(tabItems).findIndex(
          (tab) => tab.getAttribute('aria-controls') === activeTabId,
        );
        if (paramTabIndex !== -1) {
          activeTabIndex = paramTabIndex;
        }
      }
    }

    if (activeTabIndex === -1) {
      activeTabIndex = Array.from(tabItems).findIndex(
        (tab) => tab.getAttribute('aria-selected') === 'true',
      );
    }

    if (activeTabIndex === -1) {
      activeTabIndex = 0;
    }

    tabItems.forEach((tab, tabIndex) => {
      const panelId = tab.getAttribute('aria-controls')
        || (tabGroupId ? `${tabGroupId}-panel-${tabIndex}` : `panel-${tabIndex}`);
      const tabId = tab.getAttribute('id')
        || (tabGroupId ? `${tabGroupId}-tab-${tabIndex}` : `tab-${tabIndex}`);

      if (!tab.hasAttribute('role')) tab.setAttribute('role', 'tab');
      if (!tab.hasAttribute('id')) tab.setAttribute('id', tabId);
      if (!tab.hasAttribute('aria-controls')) tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('tabindex', tabIndex === activeTabIndex ? '0' : '-1');
      tab.setAttribute('aria-selected', tabIndex === activeTabIndex ? 'true' : 'false');

      // panel は aria-controls で指す ID を持つ要素を取得する（DOM 順依存を避ける）
      const panel = tabsContainer.querySelector(`#${CSS.escape(panelId)}`);
      if (panel) {
        if (!panel.hasAttribute('role')) panel.setAttribute('role', 'tabpanel');
        if (!panel.hasAttribute('id')) panel.setAttribute('id', panelId);
        if (!panel.hasAttribute('aria-labelledby')) panel.setAttribute('aria-labelledby', tabId);
        if (tabIndex !== activeTabIndex) {
          panel.setAttribute('hidden', '');
        } else {
          panel.removeAttribute('hidden');
        }
      }

      tab.addEventListener('click', () => {
        const shouldScroll = tabsContainer.classList.contains('js-tabs--scroll');
        activateTab(tabsContainer, tab, true, true, shouldScroll);
      });

      tab.addEventListener('keydown', (e) => {
        const shouldScroll = tabsContainer.classList.contains('js-tabs--scroll');

        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          const nextTab = tabItems[tabIndex + 1] || tabItems[0];
          nextTab.focus();
          activateTab(tabsContainer, nextTab, true, true, shouldScroll);
          e.preventDefault();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          const prevTab = tabItems[tabIndex - 1] || tabItems[tabItems.length - 1];
          prevTab.focus();
          activateTab(tabsContainer, prevTab, true, true, shouldScroll);
          e.preventDefault();
        } else if (e.key === 'Home') {
          tabItems[0].focus();
          activateTab(tabsContainer, tabItems[0], true, true, shouldScroll);
          e.preventDefault();
        } else if (e.key === 'End') {
          const lastTab = tabItems[tabItems.length - 1];
          lastTab.focus();
          activateTab(tabsContainer, lastTab, true, true, shouldScroll);
          e.preventDefault();
        } else if (e.key === 'Enter' || e.key === ' ') {
          activateTab(tabsContainer, tab, true, true, shouldScroll);
          e.preventDefault();
        }
      });
    });
  });

  /**
   * タブを active 状態にする
   */
  function activateTab(tabsContainer, tab, setFocus = true, updateUrl = true, shouldScroll = false) {
    const tabGroupId = tabsContainer.getAttribute('id');
    const tabItems = tabsContainer.querySelectorAll('.js-tabs__tab');

    tabItems.forEach((t) => {
      const isActive = t === tab;
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
      t.setAttribute('tabindex', isActive ? '0' : '-1');

      const panelId = t.getAttribute('aria-controls');
      const panel = panelId ? tabsContainer.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (!panel) return;

      if (isActive) {
        panel.removeAttribute('hidden');
        if (updateUrl && tabGroupId) {
          updateUrlParam(tabGroupId, panel.id);
        }
        if (shouldScroll) {
          scrollToTabs(tabsContainer);
        }
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    if (setFocus) {
      tab.focus();
    }
  }

  /**
   * URL クエリパラメータを更新（pushState で履歴に残す）
   */
  function updateUrlParam(key, value) {
    const url = new URL(window.location);
    if (url.searchParams.get(key) === value) return; // 同値なら何もしない（履歴肥大化抑止）
    url.searchParams.set(key, value);
    window.history.pushState({}, '', url);
  }

  /**
   * タブ位置までスクロール（ヘッダー高さを考慮）
   * --js-height__header と --wp-admin--admin-bar--height（WP 管理バー）を加算
   */
  function scrollToTabs(tabsContainer) {
    setTimeout(() => {
      const target = tabsContainer.querySelector('.js-tabs__tablist') || tabsContainer;
      if (!target) return;

      const rootStyles = window.getComputedStyle(document.documentElement);
      const parseVar = (name) => {
        const value = rootStyles.getPropertyValue(name).trim();
        const match = value.match(/([0-9.]+)/);
        return match ? parseFloat(match[1]) : 0;
      };

      const headerHeight = parseVar('--js-height__header') + parseVar('--wp-admin--admin-bar--height');

      const offset = target.getBoundingClientRect().top + window.scrollY - headerHeight - 20;

      window.scrollTo({
        top: offset,
        behavior: 'smooth',
      });
    }, 100);
  }

  // popstate（ブラウザ戻る/進む）対応
  window.addEventListener('popstate', () => {
    const popUrlParams = new URLSearchParams(window.location.search);

    tabGroups.forEach((tabsContainer) => {
      const tabGroupId = tabsContainer.getAttribute('id');
      if (!tabGroupId) return;

      const activePanelId = popUrlParams.get(tabGroupId);
      if (!activePanelId) return;

      const panels = tabsContainer.querySelectorAll('.js-tabs__panel');
      const tabItems = tabsContainer.querySelectorAll('.js-tabs__tab');

      const panelIndex = Array.from(panels).findIndex((panel) => panel.id === activePanelId);
      if (panelIndex !== -1 && tabItems[panelIndex]) {
        const shouldScroll = tabsContainer.classList.contains('js-tabs--scroll');
        // updateUrl=false: popstate 中に再度 pushState しない
        activateTab(tabsContainer, tabItems[panelIndex], false, false, shouldScroll);
      }
    });
  });
};

export { tabs };
